import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { FaCalendar, FaUser, FaBed, FaCheckCircle, FaClock, FaTimesCircle, FaPlus, FaEdit, FaDownload, FaSearch, FaUsers, FaMoneyBillWave } from 'react-icons/fa';
import ReservationModal from '../components/ReservationModal';
import AdminSidebar from '../components/AdminSidebar';
import html2canvas from 'html2canvas';
import ReservationSummary from '../components/ReservationSummary';

const AdminDashboard = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [reservationToDownload, setReservationToDownload] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // Nuevo estado para el término de búsqueda
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const summaryRef = useRef();
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchReservations = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReservations(data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchReservations();
  }, [checkAuth, fetchReservations]);

  useEffect(() => {
    const downloadImage = async () => {
      if (reservationToDownload && summaryRef.current) {
        try {
          // Brief delay to ensure the component is fully rendered
          await new Promise(resolve => setTimeout(resolve, 100));

          const canvas = await html2canvas(summaryRef.current, { 
            scale: 2,
            useCORS: true,
            logging: false,
           });
          const link = document.createElement('a');
          link.href = canvas.toDataURL('image/png');
          link.download = `comprobante-reserva-sumaq-mayu-${reservationToDownload.id}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error("Error generating image:", error);
          alert("No se pudo generar la imagen del comprobante.");
        } finally {
          setReservationToDownload(null); // Reset after attempting download
        }
      }
    };

    downloadImage();
  }, [reservationToDownload]);

  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('reservations')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchReservations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Reset page to 1 when filter or search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredReservations = reservations.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = searchTerm === '' ||
      r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.dni && r.dni.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReservations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReservations.length / itemsPerPage);

  const handleSetFilter = (newFilter) => {
    setFilter(newFilter);
  };

  // Dashboard Analytics Calculations - MENSUAL
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Filtrar reservas del mes actual
  const monthlyReservations = reservations.filter(r => {
    const checkinDate = new Date(r.checkin_date);
    return checkinDate.getMonth() === currentMonth && checkinDate.getFullYear() === currentYear;
  });

  const totalReservationsCount = monthlyReservations.length;
  const pendingReservationsCount = monthlyReservations.filter(r => r.status === 'pending').length;
  const confirmedReservationsCount = monthlyReservations.filter(r => r.status === 'confirmed').length;
  const totalGuestsCount = monthlyReservations.reduce((sum, r) => sum + (r.number_of_guests || 0) + (r.number_of_children || 0), 0);
  const totalRevenue = monthlyReservations
    .filter(r => r.status === 'confirmed')
    .reduce((sum, r) => sum + (parseFloat(r.total_cost) || 0), 0);

  // Obtener nombre del mes actual
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const currentMonthName = monthNames[currentMonth];


  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const icons = {
      pending: <FaClock className="inline mr-1" />,
      confirmed: <FaCheckCircle className="inline mr-1" />,
      cancelled: <FaTimesCircle className="inline mr-1" />
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {icons[status]}
        {status === 'pending' ? 'Pendiente' : status === 'confirmed' ? 'Confirmada' : 'Anulada'}
      </span>
    );
  };

  const getPaymentStatusBadge = (reservation) => {
    const total = parseFloat(reservation.total_cost) || 0;
    const paid = parseFloat(reservation.amount_paid) || 0;
    const due = total - paid;

    if (total === 0) {
      return null; // No cost, no payment status
    }
    if (due <= 0) {
      return <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded">Pagado</span>;
    }
    if (paid > 0) {
      return <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded">Pago Parcial</span>;
    }
    return <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded">Pendiente de Pago</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <AdminSidebar />
      
      <div className="flex-1 w-full md:w-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-playfair font-bold text-gray-900">
              Panel de Reservas
            </h1>
            <p className="text-xs md:text-sm text-gray-600 mt-1">Gestiona todas las reservas de habitaciones</p>
          </div>
        </div>

        <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
        {/* Month Indicator */}
        <div className="mb-4 md:mb-6">
          <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 flex items-center gap-2">
            📊 Estadísticas de {currentMonthName} {currentYear}
          </h2>
          <p className="text-xs md:text-sm text-gray-600 mt-1">Datos actualizados del mes en curso</p>
        </div>

        {/* Dashboard Analytics Section */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 md:gap-3 lg:gap-4 mb-4 md:mb-6 lg:mb-8">
          <div className="bg-white p-3 md:p-4 lg:p-5 rounded-lg shadow flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
              <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1 truncate">Total Reservas</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{totalReservationsCount}</p>
            </div>
            <FaCalendar className="text-2xl md:text-3xl lg:text-4xl text-blue-400 flex-shrink-0" />
          </div>
          <div className="bg-white p-3 md:p-4 lg:p-5 rounded-lg shadow flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
              <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1 truncate">Pendientes</p>
              <p className="text-xl md:text-2xl font-bold text-yellow-600">{pendingReservationsCount}</p>
            </div>
            <FaClock className="text-2xl md:text-3xl lg:text-4xl text-yellow-400 flex-shrink-0" />
          </div>
          <div className="bg-white p-3 md:p-4 lg:p-5 rounded-lg shadow flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
              <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1 truncate">Confirmadas</p>
              <p className="text-xl md:text-2xl font-bold text-green-600">{confirmedReservationsCount}</p>
            </div>
            <FaCheckCircle className="text-2xl md:text-3xl lg:text-4xl text-green-400 flex-shrink-0" />
          </div>
          <div className="bg-white p-3 md:p-4 lg:p-5 rounded-lg shadow flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
              <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1 truncate">Ingresos</p>
              <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 truncate">S/. {totalRevenue.toFixed(2)}</p>
            </div>
            <FaMoneyBillWave className="text-2xl md:text-3xl lg:text-4xl text-teal-400 flex-shrink-0" />
          </div>
          <div className="bg-white p-3 md:p-4 lg:p-5 rounded-lg shadow flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Mes Actual</p>
              <p className="text-xs md:text-sm text-gray-500 mb-0.5 md:mb-1 truncate">Huéspedes</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{totalGuestsCount}</p>
            </div>
            <FaUsers className="text-2xl md:text-3xl lg:text-4xl text-purple-400 flex-shrink-0" />
          </div>
        </div>

        <div className="flex flex-col gap-3 mb-4 md:mb-6">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => handleSetFilter('all')}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition whitespace-nowrap text-xs md:text-sm ${
                filter === 'all' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Todas ({filteredReservations.length})
            </button>
            <button
              onClick={() => handleSetFilter('pending')}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition whitespace-nowrap text-xs md:text-sm ${
                filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Pendientes ({reservations.filter(r => r.status === 'pending' && (searchTerm === '' || r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || (r.dni && r.dni.toLowerCase().includes(searchTerm.toLowerCase())))).length})
            </button>
            <button
              onClick={() => handleSetFilter('confirmed')}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition whitespace-nowrap text-xs md:text-sm ${
                filter === 'confirmed' ? 'bg-green-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Confirmadas ({reservations.filter(r => r.status === 'confirmed' && (searchTerm === '' || r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || (r.dni && r.dni.toLowerCase().includes(searchTerm.toLowerCase())))).length})
            </button>
            <button
              onClick={() => handleSetFilter('cancelled')}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition whitespace-nowrap text-xs md:text-sm ${
                filter === 'cancelled' ? 'bg-red-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Anuladas ({reservations.filter(r => r.status === 'cancelled' && (searchTerm === '' || r.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || (r.dni && r.dni.toLowerCase().includes(searchTerm.toLowerCase())))).length})
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Buscar por nombre o DNI..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
              />
            </div>
            
            <button
              onClick={() => {
                setEditingReservation(null);
                setShowReservationModal(true);
              }}
              className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-blue-700 transition text-xs md:text-sm font-medium whitespace-nowrap"
            >
              <FaPlus size={12} />
              <span className="hidden sm:inline">Crear Reserva</span>
              <span className="sm:hidden">Nueva</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {currentItems.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <FaCalendar className="mx-auto text-gray-300 mb-4" size={64} />
              <p className="text-gray-500 text-lg">No se encontraron reservas con los filtros actuales.</p>
            </div>
          ) : (
            currentItems.map((reservation) => (
              <div key={reservation.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 md:gap-3">
                    <div className="flex items-center gap-2 md:gap-3 min-w-0 w-full sm:w-auto">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-green-600" size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 truncate">{reservation.full_name}</h3>
                        <p className="text-xs text-gray-500 truncate">ID: {reservation.id.slice(0, 8)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">
                      {getStatusBadge(reservation.status)}
                      {getPaymentStatusBadge(reservation)}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
                    {/* Información Personal */}
                    <div className="space-y-2 md:space-y-3">
                      <h4 className="font-semibold text-gray-900 text-xs md:text-sm uppercase tracking-wide mb-2 md:mb-3 flex items-center gap-2">
                        <div className="w-1 h-3 md:h-4 bg-blue-500 rounded"></div>
                        Información Personal
                      </h4>
                      <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                        <p className="flex items-center gap-2">
                          <span className="text-gray-500 w-16 md:w-20 flex-shrink-0">DNI:</span>
                          <span className="font-medium text-gray-900 truncate">{reservation.dni}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-gray-500 w-16 md:w-20 flex-shrink-0">Teléfono:</span>
                          <span className="font-medium text-gray-900 truncate">{reservation.phone}</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="text-gray-500 w-16 md:w-20 flex-shrink-0">Email:</span>
                          <span className="font-medium text-gray-900 break-all">{reservation.email}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-gray-500 w-16 md:w-20 flex-shrink-0">Ciudad:</span>
                          <span className="font-medium text-gray-900 truncate">{reservation.city_of_origin}</span>
                        </p>
                      </div>
                    </div>

                    {/* Fechas y Huéspedes */}
                    <div className="space-y-2 md:space-y-3">
                      <h4 className="font-semibold text-gray-900 text-xs md:text-sm uppercase tracking-wide mb-2 md:mb-3 flex items-center gap-2">
                        <div className="w-1 h-3 md:h-4 bg-purple-500 rounded"></div>
                        Estadía
                      </h4>
                      <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                        <div className="bg-purple-50 p-2 md:p-3 rounded-lg">
                          <p className="text-xs text-gray-500 mb-0.5 md:mb-1">Check-in</p>
                          <p className="font-bold text-purple-700 text-xs md:text-sm">{new Date(reservation.checkin_date).toLocaleDateString('es-PE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</p>
                        </div>
                        <div className="bg-purple-50 p-2 md:p-3 rounded-lg">
                          <p className="text-xs text-gray-500 mb-0.5 md:mb-1">Check-out</p>
                          <p className="font-bold text-purple-700 text-xs md:text-sm">{new Date(reservation.checkout_date).toLocaleDateString('es-PE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</p>
                        </div>
                        <div className="flex gap-2 mt-2 md:mt-3">
                          <div className="flex-1 bg-blue-50 p-2 rounded-lg text-center">
                            <p className="text-xs text-gray-500">Adultos</p>
                            <p className="font-bold text-blue-700 text-base md:text-lg">{reservation.number_of_guests}</p>
                          </div>
                          <div className="flex-1 bg-blue-50 p-2 rounded-lg text-center">
                            <p className="text-xs text-gray-500">Niños</p>
                            <p className="font-bold text-blue-700 text-base md:text-lg">{reservation.number_of_children}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Habitaciones y Pagos */}
                    <div className="space-y-2 md:space-y-3">
                      <h4 className="font-semibold text-gray-900 text-xs md:text-sm uppercase tracking-wide mb-2 md:mb-3 flex items-center gap-2">
                        <div className="w-1 h-3 md:h-4 bg-green-500 rounded"></div>
                        Habitaciones & Pago
                      </h4>
                      <div className="bg-green-50 p-3 md:p-4 rounded-lg space-y-1.5 md:space-y-2">
                        <div className="flex items-center gap-2 mb-1 md:mb-2">
                          <FaBed className="text-green-600 flex-shrink-0" size={16} />
                          <span className="font-semibold text-gray-900 text-sm md:text-base">Habitaciones</span>
                        </div>
                        {reservation.assigned_room_number && (
                          <div className="bg-white px-2 md:px-3 py-1.5 md:py-2 rounded border border-green-200">
                            <p className="text-xs text-gray-500">Número Asignado</p>
                            <p className="font-bold text-green-700 text-sm md:text-base">#{reservation.assigned_room_number}</p>
                          </div>
                        )}
                        {reservation.room_types?.map((room, idx) => (
                          <p key={idx} className="text-xs md:text-sm text-gray-700">
                            • <span className="font-medium">{room.type}</span>: {room.quantity} hab.
                          </p>
                        )) || (
                          <p className="text-xs md:text-sm text-gray-700">
                            • <span className="font-medium">{reservation.room_type}</span>: {reservation.number_of_rooms} hab.
                          </p>
                        )}
                      </div>
                      
                      {reservation.total_cost > 0 && (
                        <div className="space-y-1.5 md:space-y-2">
                          <div className="flex justify-between items-center text-xs md:text-sm">
                            <span className="text-gray-600">Total:</span>
                            <span className="font-bold text-gray-900 text-base md:text-lg">S/. {parseFloat(reservation.total_cost).toFixed(2)}</span>
                          </div>
                          {reservation.amount_paid > 0 && (
                            <div className="flex justify-between items-center text-xs md:text-sm">
                              <span className="text-gray-600">Pagado:</span>
                              <span className="font-medium text-green-600">S/. {parseFloat(reservation.amount_paid).toFixed(2)}</span>
                            </div>
                          )}
                          {reservation.amount_due > 0 && (
                            <div className="flex justify-between items-center text-xs md:text-sm pt-1.5 md:pt-2 border-t border-gray-200">
                              <span className="text-gray-600">Adeudado:</span>
                              <span className="font-bold text-red-600 text-base md:text-lg">S/. {parseFloat(reservation.amount_due).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-3 md:pt-4 border-t border-gray-200">
                    {reservation.status === 'pending' && (
                      <>
                        <button 
                          onClick={() => updateStatus(reservation.id, 'confirmed')} 
                          className="flex items-center gap-1.5 md:gap-2 bg-green-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-green-600 transition-colors"
                        >
                          <FaCheckCircle size={12} />
                          Confirmar
                        </button>
                        <button 
                          onClick={() => updateStatus(reservation.id, 'cancelled')} 
                          className="flex items-center gap-1.5 md:gap-2 bg-red-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-red-600 transition-colors"
                        >
                          <FaTimesCircle size={12} />
                          Anular
                        </button>
                      </>
                    )}
                    <button 
                      onClick={() => setReservationToDownload(reservation)} 
                      className="flex items-center gap-1.5 md:gap-2 bg-blue-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-blue-600 transition-colors"
                    >
                      <FaDownload size={12} />
                      <span className="hidden sm:inline">Descargar</span>
                      <span className="sm:hidden">PDF</span>
                    </button>
                    <button 
                      onClick={() => { setEditingReservation(reservation); setShowReservationModal(true);}} 
                      className="flex items-center gap-1.5 md:gap-2 bg-gray-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-gray-700 transition-colors"
                    >
                      <FaEdit size={12} />
                      Editar
                    </button>
                  </div>

                  {/* Footer */}
                  <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-gray-100 text-xs text-gray-400">
                    Creada el {new Date(reservation.created_at).toLocaleString('es-PE')}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {totalPages > 1 && (
          <div className="mt-6 md:mt-8 bg-white rounded-lg shadow-sm border border-gray-200 px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs md:text-sm text-gray-600 text-center sm:text-left">
              Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredReservations.length)} de {filteredReservations.length}
            </p>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-white border border-gray-300 rounded-lg text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-xs md:text-sm text-gray-700 font-medium whitespace-nowrap">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 md:px-4 py-1.5 md:py-2 bg-white border border-gray-300 rounded-lg text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
      
      <ReservationModal
        isOpen={showReservationModal}
        onClose={() => {
          setShowReservationModal(false);
          setEditingReservation(null);
          fetchReservations();
        }}
        isAdmin={true}
        existingReservation={editingReservation}
      />

      {/* Hidden component for rendering summary to image */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: -1 }}>
        <ReservationSummary ref={summaryRef} reservation={reservationToDownload} />
      </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
